<template>
  <div>
    <account-verification-modal
        :is-dialog-visible="isAccountVerificationModalVisible"
        :is-verification-override="false"
        @visibilityChanged="setIsAccountVerificationModalVisible" />
    <account-verification-modal
        :is-dialog-visible="isAccountVerificationOverrideModalVisible"
        :is-verification-override="true"
        @visibilityChanged="setIsAccountVerificationOverrideModalVisible" />

    <revoke-verification-modal
        :is-dialog-visible="isAuthorizationRevokeModalVisible"
        @visibilityChanged="setIsAuthorizationRevokeModalVisible" />

    <div class="ring-card-action-box grid gap-no">
      <div class="span-1">
        <img
            :src="icons.accountVerification"
            width="32">
      </div>
      <div class="span-8">
        <!-- Left side information -->
        <div class="ring-card-action-box__text ring-card-action-box__title">
          Account Verification
        </div>
        <div v-if="pii.authorization.status">
          <div>
            <slot>
              <div class="flex flex-col">
                <div
                    class="justify-between content-center"
                    style="color: #999999">
                  <span
                      v-if="pii.authorization.expires_at"
                      class="ring-card-action-box__text ring-card-action-box__description">
                    Expires at: &nbsp;
                    <time-zone-formatter :current-time="pii.authorization.expires_at" />
                  </span>
                  <span
                      v-else
                      class="ring-card-action-box__text ring-card-action-box__description">
                    Created at:
                    <span>
                      <time-zone-formatter :current-time="pii.authorization.created_at" />
                    </span>
                  </span>
                </div>
              </div>
            </slot>
          </div>
        </div>
        <div style="margin-top: 12px">
          <div
              v-if="!pii.authorization.status"
              class="ring-card-action-box__text ring-card-action-box__text-error">
            Verification Required
          </div>
          <div
              v-else-if="pii.authorization.status === 'active'"
              class="ring-card-action-box__text-success ring-card-action-box__text">
            Account Verified
          </div>
          <div
              v-else
              class="text-info"
              style="color: orange">
            Verification Status: {{ pii.authorization.status | sentenceCase }}
          </div>
        </div>
      </div>

      <!-- Account verification buttons -->
      <div
          class="span-3"
          :class="verticalAlignment">
        <div class="customer-field">
          <!-- New UI Account Verification flow -->
          <div v-if="!pii.authorization.status">
            <el-button
                :disabled="disableVerifyAccount || isLocked || pii.authorization.status === 'active'"
                style="margin-bottom: 10px"
                class="custom-el-button"
                size="small"
                type="primary"
                plain
                @click="showAccountVerificationModal">
              Verify Account
            </el-button>
            <LinkButton
                v-acl.show="{permission: 'overrideAuth', on: 'Customer'}"
                :disabled="disableVerifyAccount"
                @click="showAccountOverrideVerificationModal">
              Verify Override
            </LinkButton>
          </div>

          <div v-if="pii.authorization.status === 'active'">
            <el-button
                slot="reference"
                class="custom-el-button"
                size="small"
                style="margin-bottom: 10px"
                plain
                type="primary"
                @click="showRevokeAuthorizationModal">
              Revoke Auth
            </el-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { get } from "lodash";

import NewRingAuthRequest from "@/containers/Customer/Authorizations/NewRingAuthRequest";
import AccountVerificationModal from "../../../NewUI/components/customer/authorizations/shared/AccountVerificationModal";
import RevokeVerificationModal from "../../../NewUI/components/customer/authorizations/revoke/RevokeVerificationModal";
import { createNamespacedHelpers as module } from "vuex";
import permission from "@/mixins/permission";
import LinkButton from "../../../NewUI/components/core/LinkButton";

const {
  mapGetters: accountGetters,
  mapActions: accountActions,
  mapMutations: accountMutations,
} = module("db/account");

export default {
  name: "RingAuthStatus",
  components: {
    LinkButton,
    NewRingAuthRequest,
    RevokeVerificationModal,
    AccountVerificationModal,
  },
  mixins: [permission],
  data() {
    return {
      icons: {
        accountVerification: require("@/assets/icons/action-account-verification.svg"),
      },
      revokeConfirmation: false,
      pii: {
        authorization: {
          status: "loading...",
        },
        ajax: {
          working: false,
        },
      },
      isAccountVerificationModalVisible: false,
      isAccountVerificationOverrideModalVisible: false,
      isAuthorizationRevokeModalVisible: false,
    };
  },
  computed: {
    ...accountGetters([
      "customer",
      "customerId",
      "piiOff",
      "piiOn",
      "isLocked",
      "channels",
    ]),
    disableVerifyAccount() {
      return Boolean(this.pii.authorization.status);
    },
    verticalAlignment() {
      return "column-centered";
    },
  },
  created() {
    this.fetchPIIAuthorization();
  },
  updated() {
    if (this.pii.authorization.status === "pending") {
      document.getElementById("enterCode").click();
    }
  },
  methods: {
    verifyCustomerAuth(req) {
      this.pii.authorization = req;
    },
    fetchPIIAuthorization() {
      this.pii.ajax.working = true;
      axios
        .get(`v2/customers/${this.customerId}/pii_authorization_status`)
        .then((response) => {
          this.pii.authorization = get(response, "data.authorization", {});

          this.ignorePendingStatusForNewVerificationFlow();
        })
        .catch((error) => console.error(error))
        .finally(() => (this.pii.ajax.working = false));
    },
    resendPIICode() {
      return axios
        .post(`v2/customers/${this.customerId}/resend_pii_code.json`)
        .then((response) => {
          this.$notify({
            title: "Success",
            message: "Pending for approval",
            type: "success",
          });
        })
        .catch((error) => {
          this.$notify({
            title: "Error",
            message: "Something went wrong! Try again.",
            type: "error",
          });
        });
    },
    ignorePendingStatusForNewVerificationFlow() {
      if (this.pii.authorization.status === "pending") {
        this.pii.authorization.status = null;
      }
    },
    sendRevokeRequest() {
      this.revokeConfirmation = false;
      this.pii.ajax.working = true;
      return axios
        .post(`v2/customers/${this.customerId}/revoke_auth_request`)
        .then((response) => {
          this.$notify({
            title: "Success",
            message: "Request Auth Revoked",
            type: "success",
          });
        })
        .catch((error) => {
          this.$notify({
            title: "Error",
            message: "Something went wrong! Try again.",
            type: "error",
          });
        })
        .finally(() => {
          this.pii.ajax.working = false;
          this.$router.go(this.$router.currentRoute);
        });
    },
    expireAuthenticationRequest() {
      this.revokeConfirmation = false;
      this.pii.ajax.working = true;
      return axios
        .post(`v2/customers/${this.customerId}/invalidate_pending_auth_request`)
        .then((response) => {
          this.pii.authorization.status = null;
        })
        .catch((error) => {
          this.$notify({
            title: "Error",
            message: "Something went wrong! Try again.",
            type: "error",
          });
        })
        .finally(() => {
          this.pii.ajax.working = false;
        });
    },
    overrideCustomerAuth() {
      return axios
        .post(
          `v2/customers/${this.customerId}/personally_identifiable_information_override.json`
        )
        .then((response) => {
          this.$notify({
            title: "Success",
            message: "Browser will reload shortly!",
            type: "success",
          });
          this.$router.go(this.$router.currentRoute);
        })
        .catch((error) => {
          this.$notify({
            title: "Error",
            message: "Something went wrong! Try again.",
            type: "error",
          });
        });
    },
    showAccountVerificationModal() {
      this.isAccountVerificationModalVisible = true;
    },
    showAccountOverrideVerificationModal() {
      this.isAccountVerificationOverrideModalVisible = true;
    },
    showRevokeAuthorizationModal() {
      this.isAuthorizationRevokeModalVisible = true;
    },
    setIsAccountVerificationModalVisible(value) {
      this.isAccountVerificationModalVisible = value;
    },
    setIsAccountVerificationOverrideModalVisible(value) {
      this.isAccountVerificationOverrideModalVisible = value;
    },
    setIsAuthorizationRevokeModalVisible(value) {
      this.isAuthorizationRevokeModalVisible = value;
    },
  },
};
</script>

<style scoped>
.verify-account-status__alert > .el-alert__content {
  color: $--color-dusty-gray;
}

.verify-account-status__buttons {
  button.el-button.el-button--text[type="button"] {
    /*font-size: small;*/
    &.el-popover__reference {
      font-size: small;
    }
  }
}

.column-centered {
  display: flex;
  justify-content: center;
}
</style>
